import { Button as ChakraButton, type ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

// Button variant styles using design tokens
const buttonVariantStyles = {
  solid: {
    brand: {
      bg: "brand.600",
      color: "white",
      _hover: { bg: "brand.700" },
      _active: { bg: "brand.800" },
    },
    success: {
      bg: "success.solid",
      color: "white",
      _hover: { bg: "success.solid", filter: "brightness(0.9)" },
      _active: { bg: "success.solid", filter: "brightness(0.8)" },
    },
    error: {
      bg: "error.solid",
      color: "white",
      _hover: { bg: "error.dark" },
      _active: { bg: "error.dark", filter: "brightness(0.9)" },
    },
    warning: {
      bg: "warning.solid",
      color: "white",
      _hover: { bg: "warning.solid", filter: "brightness(0.9)" },
      _active: { bg: "warning.solid", filter: "brightness(0.8)" },
    },
    info: {
      bg: "info.solid",
      color: "white",
      _hover: { bg: "info.solid", filter: "brightness(0.9)" },
      _active: { bg: "info.solid", filter: "brightness(0.8)" },
    },
  },
  outline: {
    brand: {
      borderColor: "brand.600",
      color: "brand.600",
      _hover: { bg: "brand.50", _dark: { bg: "brand.900" } },
      _active: { bg: "brand.100", _dark: { bg: "brand.800" } },
    },
    error: {
      borderColor: "error.solid",
      color: "error.solid",
      _hover: { bg: "error.subtle" },
      _active: { bg: "error.muted" },
    },
  },
  ghost: {
    brand: {
      color: "brand.600",
      _hover: { bg: "brand.50", _dark: { bg: "brand.900" } },
      _active: { bg: "brand.100", _dark: { bg: "brand.800" } },
    },
    error: {
      color: "error.solid",
      _hover: { bg: "error.subtle" },
      _active: { bg: "error.muted" },
    },
  },
};

export interface ButtonProps extends Omit<ChakraButtonProps, 'colorScheme'> {
  colorScheme?: 'brand' | 'success' | 'error' | 'warning' | 'info' | 'gray';
  variant?: 'solid' | 'outline' | 'ghost';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({
    colorScheme = 'brand',
    variant = 'solid',
    ...props
  }, ref) {
    
    // Get custom styles for our color schemes
    const getCustomStyles = () => {
      if (colorScheme === 'gray') {
        // Use default Chakra gray styles
        return { colorScheme: 'gray' };
      }
      
      const variantStyles = buttonVariantStyles[variant];
      if (variantStyles && colorScheme in variantStyles) {
        return variantStyles[colorScheme as keyof typeof variantStyles];
      }
      
      // Fallback to default styles
      return buttonVariantStyles.solid.brand;
    };
    
    const customStyles = getCustomStyles();
    const isGrayScheme = colorScheme === 'gray';
    
    return (
      <ChakraButton
        ref={ref}
        variant={variant}
        {...(isGrayScheme ? { colorScheme: 'gray' } : customStyles)}
        {...props}
      />
    );
  }
);

export default Button; 